/**
 * Google Maps Distance Matrix Service Utility
 * ใช้สำหรับคำนวณระยะทางจริงตามถนน - เส้นทางที่สั้นที่สุด
 * DistanceMatrixService จะคำนวณเส้นทางที่สั้นที่สุดโดยอัตโนมัติเมื่อใช้ DRIVING mode
 */

// Cache สำหรับเก็บผลลัพธ์ (key: "lat1,lng1|lat2,lng2", value: distance in km)
const distanceCache = new Map();

/**
 * ตรวจสอบว่า Google Maps API พร้อมใช้งานหรือไม่
 */
function isGoogleMapsReady() {
  return typeof window !== 'undefined' && 
         window.google && 
         window.google.maps && 
         window.google.maps.DistanceMatrixService &&
         window.google.maps.DirectionsService;
}

/**
 * สร้าง cache key จากพิกัด
 */
function getCacheKey(origin, destination) {
  return `${origin.lat},${origin.lng}|${destination.lat},${destination.lng}`;
}

/**
 * คำนวณระยะทางเส้นทางที่สั้นที่สุดโดยใช้ DirectionsService
 * @param {Object} origin - { lat: number, lng: number }
 * @param {Object} destination - { lat: number, lng: number }
 * @returns {Promise<number|null>} - ระยะทางเป็นกิโลเมตร หรือ null ถ้าไม่สามารถคำนวณได้
 */
function calculateShortestDistanceWithDirections(origin, destination) {
  return new Promise((resolve) => {
    try {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: origin,
          destination: destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
          provideRouteAlternatives: true, // ขอเส้นทางหลายเส้นทาง
          avoidHighways: false, // ไม่หลีกเลี่ยงทางหลวง (เพื่อให้ได้เส้นทางที่สั้นที่สุด)
          avoidTolls: false, // ไม่หลีกเลี่ยงทางด่วน (เพื่อให้ได้เส้นทางที่สั้นที่สุด)
          optimizeWaypoints: false,
        },
        (result, status) => {
          if (status === 'OK' && result.routes && result.routes.length > 0) {
            // หาเส้นทางที่สั้นที่สุดจากหลายเส้นทาง
            let shortestDistance = Infinity;
            result.routes.forEach(route => {
              let totalDistance = 0;
              route.legs.forEach(leg => {
                if (leg.distance && leg.distance.value) {
                  totalDistance += leg.distance.value;
                }
              });
              if (totalDistance < shortestDistance) {
                shortestDistance = totalDistance;
              }
            });
            
            if (shortestDistance !== Infinity) {
              const distanceKm = shortestDistance / 1000;
              resolve(distanceKm);
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        }
      );
    } catch (error) {
      console.warn('DirectionsService error:', error);
      resolve(null);
    }
  });
}

/**
 * คำนวณระยะทางระหว่างจุดสองจุด (single)
 * ใช้ DirectionsService เพื่อหาเส้นทางที่สั้นที่สุด และ fallback ไปใช้ DistanceMatrixService
 * @param {Object} origin - { lat: number, lng: number }
 * @param {Object} destination - { lat: number, lng: number }
 * @returns {Promise<number|null>} - ระยะทางเป็นกิโลเมตร หรือ null ถ้าไม่สามารถคำนวณได้
 */
export function calculateDistance(origin, destination) {
  if (!origin || !destination || 
      typeof origin.lat !== 'number' || typeof origin.lng !== 'number' ||
      typeof destination.lat !== 'number' || typeof destination.lng !== 'number') {
    return Promise.resolve(null);
  }

  // ตรวจสอบ cache
  const cacheKey = getCacheKey(origin, destination);
  if (distanceCache.has(cacheKey)) {
    return Promise.resolve(distanceCache.get(cacheKey));
  }

  // ถ้า Google Maps API ยังไม่พร้อม return null
  if (!isGoogleMapsReady()) {
    console.warn('Google Maps API is not ready');
    return Promise.resolve(null);
  }

  // ใช้ DirectionsService เพื่อหาเส้นทางที่สั้นที่สุด
  return calculateShortestDistanceWithDirections(origin, destination)
    .then(distance => {
      if (distance !== null) {
        distanceCache.set(cacheKey, distance);
        return distance;
      }
      
      // ถ้า DirectionsService ไม่ได้ผล ใช้ DistanceMatrixService
      return new Promise((resolve) => {
        try {
          const service = new window.google.maps.DistanceMatrixService();
          service.getDistanceMatrix(
            {
              origins: [origin],
              destinations: [destination],
              travelMode: window.google.maps.TravelMode.DRIVING,
              unitSystem: window.google.maps.UnitSystem.METRIC,
            },
            (response, status) => {
              if (status === 'OK' && response.rows[0] && response.rows[0].elements[0]) {
                const element = response.rows[0].elements[0];
                if (element.status === 'OK') {
                  // แปลงจากเมตรเป็นกิโลเมตร
                  const distanceKm = element.distance.value / 1000;
                  distanceCache.set(cacheKey, distanceKm);
                  resolve(distanceKm);
                } else {
                  // ถ้า API error return null
                  console.warn('DistanceMatrixService error:', element.status);
                  resolve(null);
                }
              } else {
                // ถ้า API error return null
                console.warn('DistanceMatrixService error:', status);
                resolve(null);
              }
            }
          );
        } catch (error) {
          console.warn('DistanceMatrixService error:', error);
          resolve(null);
        }
      });
    })
    .catch(error => {
      console.warn('Error calculating shortest distance:', error);
      return null;
    });
}

/**
 * คำนวณระยะทางหลายจุดพร้อมกัน (batch) - ใช้ DirectionsService เพื่อหาเส้นทางที่สั้นที่สุด
 * สำหรับ destinations น้อย (≤10) จะใช้ DirectionsService แบบ parallel
 * สำหรับ destinations มาก (>10) จะใช้ DistanceMatrixService (เร็วกว่าแต่ไม่ได้เส้นทางที่สั้นที่สุดเสมอ)
 * @param {Object} origin - { lat: number, lng: number }
 * @param {Array<Object>} destinations - [{ lat: number, lng: number }, ...]
 * @returns {Promise<Array<number|null>>} - array of distances in km
 */
export function calculateDistancesBatch(origin, destinations) {
  if (!origin || !destinations || !Array.isArray(destinations) || destinations.length === 0) {
    return Promise.resolve([]);
  }

  // กรอง destinations ที่มีพิกัดถูกต้อง
  const validDestinations = destinations.filter(d => 
    d && typeof d.lat === 'number' && typeof d.lng === 'number'
  );

  if (validDestinations.length === 0) {
    return Promise.resolve(destinations.map(() => null));
  }

  // ตรวจสอบ cache และแยกเป็น cached และ uncached
  const results = [];
  const uncachedDestinations = [];
  const uncachedIndices = [];

  destinations.forEach((dest, index) => {
    if (!dest || typeof dest.lat !== 'number' || typeof dest.lng !== 'number') {
      results[index] = null;
      return;
    }

    const cacheKey = getCacheKey(origin, dest);
    if (distanceCache.has(cacheKey)) {
      results[index] = distanceCache.get(cacheKey);
    } else {
      uncachedDestinations.push(dest);
      uncachedIndices.push(index);
    }
  });

  // ถ้าทุกอันอยู่ใน cache แล้ว return ทันที
  if (uncachedDestinations.length === 0) {
    return Promise.resolve(results);
  }

  // ถ้า Google Maps API ยังไม่พร้อม return null สำหรับ uncached
  if (!isGoogleMapsReady()) {
    console.warn('Google Maps API is not ready');
    uncachedDestinations.forEach((dest, i) => {
      results[uncachedIndices[i]] = null;
    });
    return Promise.resolve(results);
  }

  // สำหรับ destinations น้อย (≤10) ใช้ DirectionsService แบบ parallel เพื่อหาเส้นทางที่สั้นที่สุด
  if (uncachedDestinations.length <= 10) {
    const distancePromises = uncachedDestinations.map(dest => {
      const cacheKey = getCacheKey(origin, dest);
      return calculateShortestDistanceWithDirections(origin, dest)
        .then(distance => {
          if (distance !== null) {
            distanceCache.set(cacheKey, distance);
            return distance;
          }
          // ถ้า DirectionsService ล้มเหลว return null
          return null;
        })
        .catch(error => {
          console.warn('DirectionsService error for destination:', error);
          return null;
        });
    });

    return Promise.all(distancePromises)
      .then(distances => {
        distances.forEach((distance, i) => {
          results[uncachedIndices[i]] = distance;
        });
        return results;
      });
  }

  // สำหรับ destinations มาก (>10) ใช้ DistanceMatrixService (เร็วกว่า)
  return new Promise((resolve) => {
    try {
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [origin],
          destinations: uncachedDestinations,
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
        },
        (response, status) => {
          if (status === 'OK' && response.rows[0] && response.rows[0].elements) {
            response.rows[0].elements.forEach((element, i) => {
              const dest = uncachedDestinations[i];
              const cacheKey = getCacheKey(origin, dest);
              const originalIndex = uncachedIndices[i];

              if (element.status === 'OK') {
                const distanceKm = element.distance.value / 1000;
                distanceCache.set(cacheKey, distanceKm);
                results[originalIndex] = distanceKm;
              } else {
                // ถ้า API error return null
                console.warn('DistanceMatrixService error for destination:', element.status);
                results[originalIndex] = null;
              }
            });
          } else {
            // ถ้า API error return null สำหรับทั้งหมด
            console.warn('DistanceMatrixService error:', status);
            uncachedDestinations.forEach((dest, i) => {
              results[uncachedIndices[i]] = null;
            });
          }
          resolve(results);
        }
      );
    } catch (error) {
      console.warn('DistanceMatrixService batch error:', error);
      // return null สำหรับทั้งหมด
      uncachedDestinations.forEach((dest, i) => {
        results[uncachedIndices[i]] = null;
      });
      resolve(results);
    }
  });
}

/**
 * ล้าง cache
 */
export function clearDistanceCache() {
  distanceCache.clear();
}

